import {
  Button,
  Card,
  Container,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import { debounce } from 'lodash';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PageHeader from 'components/PageHeader/PageHeader';
import SearchNotFound from 'components/table/SearchNoFound';
import TableHeader from 'components/table/TableHeader';
import TableToolbar from 'components/table/TableToolbar';
import { getCompobotConfigTableHead } from 'constans/table-headers';
import SnackbarContext from 'contexts/snackbarContext';
import {
  handleSelectAllClick,
  sortByColumn,
} from 'services/list.service';
import {
  getCompobotConfigForAdmin,
  getCompobotConfigsForAdmin,
  editCompobotConfig
} from 'services/compobotGuiVersions.service';
import { CompobotConfigType } from 'types/compobotGuiVersion.type';
import { Link as RouterLink } from 'react-router-dom';

const normalizeConfigRows = (data: CompobotConfigType | CompobotConfigType[]) => {
  const rows = Array.isArray(data) ? data : [data];

  return rows.map((config) => ({
    ...config,
    id: config.id || config.deviceId || '',
  }));
};

const getErrorMessage = (error: { data?: { message?: string } }) =>
  error.data?.message || 'Something went wrong';

export default function CompoBotConfigPage() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [selected, setSelected] = useState<string[]>([]);
  const [orderBy, setOrderBy] = useState('name');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [cbConfigList, setCbConfigList] = useState<CompobotConfigType[]>([]);
  const { handleOpen } = useContext(SnackbarContext);

  useEffect(() => {
    getConfigHandler();
  }, []);

  const getConfigHandler  = () => {
    getCompobotConfigsForAdmin()
      .then((response) => {
        setCbConfigList(normalizeConfigRows(response.data));
      })
      .catch((error) => {
        handleOpen(getErrorMessage(error));
      });
  };

  const searchFilter = useCallback(
    debounce((filterName) => {
      if (filterName) {
        getCompobotConfigForAdmin(filterName)
          .then((response) => {
            setCbConfigList(normalizeConfigRows(response.data));
          })
          .catch((error) => {
            handleOpen(getErrorMessage(error));
          });
      } else {
        getConfigHandler();
      }
    }, 300),
    [],
  );

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const sortedCbConfig = sortByColumn(
    cbConfigList,
    orderBy as keyof CompobotConfigType,
    order,
  );

  const cbConfigTableHead = useMemo(getCompobotConfigTableHead, [language]);

  return (
    <Container>
      <PageHeader 
        title={t('compobotConfigPage.title')} 
        hasButton={false}
      />

      <Button 
        variant="outlined" sx={{ margin: '0px 5px 20px 5px' }} component={RouterLink} to={'/gui-versions'}>
        {t('compobotConfigPage.compobotGuiVersionButton')}
      </Button>

      <Card>
        <TableToolbar
          numSelected={selected.length}
          onFilterName={searchFilter}
          deleteHandler={() => null}
        />

        <TableContainer>
          <Table>
            {
              <TableHeader
                order={order}
                orderBy={orderBy}
                headLabel={cbConfigTableHead}
                rowCount={cbConfigList.length}
                numSelected={selected.length}
                onRequestSort={handleRequestSort}
                onSelectAllClick={(checked) =>
                  setSelected(handleSelectAllClick(checked, cbConfigList))
                }
              />
            }
            <TableBody>
              {sortedCbConfig
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  const {
                    id,
                    compobot_name,
                    lang,
                    queryFreq,
                    timeZone,
                    version_name,
                  } = row;

                  return (
                    <TableRow
                      hover
                      key={id}
                      tabIndex={-1}
                    >
                      <TableCell component="th" scope="row" padding="none">
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography
                            variant="subtitle2"
                            noWrap
                            sx={{ fontWeight: 600 }}
                          >
                            {compobot_name}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell align="left">{lang}</TableCell>
                      <TableCell align="left">{queryFreq}</TableCell>
                      <TableCell align="left">{timeZone}</TableCell>
                      <TableCell align="left">{version_name}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
            {!cbConfigList.length && (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                    <SearchNotFound />
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={cbConfigList.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, page) => setPage(page)}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('pagination.rowsPerPage')}
        />
      </Card>
    </Container>
  );
}
